// One-line summary for a collapsed specialist card header. The specialist's
// reply is markdown written for a full-width view; the collapsed row has one
// truncated line, so it needs the plainest sentence in the reply rather than
// the first raw line (which in practice is often a horizontal rule, bold
// "**RELEVANCE:**" markup, or the specialist narrating its own next step).
// The open card body and dialog still render the untouched markdown answer.

export type SpecialistPhase = 'bid' | 'assignment';

const RELEVANCE_LABELS: Record<string, string> = {
  'not relevant': 'Not relevant',
  'conditionally relevant': 'Conditionally relevant',
  relevant: 'Relevant',
};

// Matches the bid reply contract in src/agenthub/runtime/argus/tools.py
// (`RELEVANCE: relevant | conditionally relevant | not relevant`). Bold or
// list markup around the key is stripped before this runs.
const RELEVANCE_LINE = /^relevance:\s*(not relevant|conditionally relevant|relevant)\b\s*[-–—:,.]?\s*(.*)$/i;

// Only lines that are entirely a thematic break or punctuation filler:
// `---`, `***`, `___`, `- - -`, `...`, `—`.
const FILLER_LINE = /^[\s\-*_.=~—–|]*$/;

// First-person process narration a specialist emits between tool calls. The
// list is intentionally short: it covers the phrasings seen in the 2026-09-18
// staging transcripts, and if every line matches we still fall back to the
// first clean line so the header is never empty.
const SCRATCH_OPENERS = [
  /^let me\b/i,
  /^i(?:'ll| will| need| am| can|'m going to)\b/i,
  /^now (?:querying|pulling|checking|retrying|searching)\b/i,
  /^(?:pulling|querying|retrying|fetching|searching|checking|looking up)\b/i,
  /^no (?:matches|results?|record).*\blet me\b/i,
];

const MAX_REASON_LENGTH = 80;

function stripInlineMarkdown(line: string): string {
  return line
    .replace(/^\s{0,3}#{1,6}\s+/, '') // heading marker
    .replace(/^\s*(?:[-*+]|\d+[.)])\s+/, '') // list bullet / ordered marker
    .replace(/^\s*>\s?/, '') // blockquote
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/(^|[^*])\*(?!\s)([^*]+?)\*(?!\*)/g, '$1$2')
    .replace(/(^|[^_])_(?!\s)([^_]+?)_(?!_)/g, '$1$2')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1') // link text only
    .replace(/[*_`]+$/g, '') // dangling closing markers left mid-stream
    .replace(/\s+/g, ' ')
    .trim();
}

function cleanLines(text: string): string[] {
  const lines: string[] = [];
  for (const raw of text.split(/\r?\n/)) {
    if (FILLER_LINE.test(raw)) continue;
    const line = stripInlineMarkdown(raw);
    if (line && !FILLER_LINE.test(line)) lines.push(line);
  }
  return lines;
}

function isScratch(line: string): boolean {
  return SCRATCH_OPENERS.some((pattern) => pattern.test(line));
}

function relevanceSummary(lines: readonly string[]): string | null {
  for (const line of lines) {
    const match = RELEVANCE_LINE.exec(line);
    if (!match) continue;
    const label = RELEVANCE_LABELS[match[1].toLowerCase()];
    const reason = match[2].trim();
    if (!reason) return label;
    const clipped = reason.length > MAX_REASON_LENGTH ? `${reason.slice(0, MAX_REASON_LENGTH - 1).trimEnd()}…` : reason;
    return `${label} – ${clipped}`;
  }
  return null;
}

/**
 * Reduce a specialist reply to a single header-safe line, or `null` when the
 * reply has no usable text. Bid replies collapse to their RELEVANCE verdict;
 * assignment replies use the first line that is neither markdown filler nor
 * first-person narration, falling back to the first clean line.
 */
export function summarizeSpecialistAnswer(answer: string | null | undefined, phase: SpecialistPhase): string | null {
  if (!answer) return null;
  const lines = cleanLines(answer);
  if (lines.length === 0) return null;
  if (phase === 'bid') {
    const verdict = relevanceSummary(lines);
    if (verdict) return verdict;
  }
  return lines.find((line) => !isScratch(line)) ?? lines[0];
}
